import { Link } from "react-router-dom";
import { ArrowUpRight, PencilRuler, ShieldCheck, Hammer, Sparkles, Star } from "lucide-react";
import { useEffect, useState } from "react";
import { api, BUSINESS } from "@/lib/sleek";

const HERO = "/photos/blumwayland/chef_hero_hires.webp";

const STATS = [
  { k: "27+", v: "Years remodeling MetroWest homes" },
  { k: "3", v: "Cabinet lines with countless color & stain options" },
  { k: "4.5★", v: "Avg. owner review (Angi)" },
  { k: "1", v: "Walk-in Northborough showroom" },
];

const TESTIMONIALS = [
  { name: "Marta R.", town: "Revere, MA", text: "From the first showroom visit to the final punch list, Sleek made a major remodel feel effortless. The cabinetry detailing is best-in-class." },
  { name: "Carl B.", town: "Wayland, MA", text: "I've done business with Chris and his crew at Sleek Renovations for several years. They have done jobs from substantial kitchen and bathroom updates and 3-season porch renovation to deck staining, fence repair, ice dam removal, and interior and exterior painting--to name just a few--and the work is always outstanding... My most important recommendation for this business is that Chris and his workers are honest and trustworthy, and personally committed to delivering excellent work. I trust all his people in my house when I am not home, and I feel confident that work will always be done to the highest standard." },
  { name: "Jennifer", town: "Concord, MA", text: "I would highly recommend Sleek Renovation - Chris and his crew. The work is impeccable, to perfection and beautiful... Chris and crew - are all very nice and helpful." },
];

export default function Home() {
  const [portfolio, setPortfolio] = useState([]);
  const [pfStats, setPfStats] = useState(null);
  useEffect(() => {
    api.get("/portfolio").then((r) => setPortfolio(r.data.slice(0, 5))).catch(() => {});
    api.get("/portfolio-stats").then((r) => setPfStats(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-end overflow-hidden">
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-black/10" />
        <div className="relative z-10 w-full mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 pb-20 md:pb-28 text-white fade-up">
          <div className="text-[11px] tracking-[0.4em] uppercase opacity-90 mb-6">
            Sleek Renovations · Sleek Kitchen &amp; Bath
          </div>
          <h1 className="font-display text-5xl md:text-7xl lg:text-8xl font-light leading-[0.95] max-w-5xl">
            Kitchens, baths,<br />and whole-home<br />remodels — built with<br /><em className="not-italic font-normal">precision</em>.
          </h1>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/booking" data-testid="hero-cta-booking" className="bg-white text-zinc-950 px-7 py-4 text-sm font-medium hover:bg-zinc-100 transition flex items-center gap-2">
              Book a free consultation <ArrowUpRight size={16} />
            </Link>
            <Link to="/showroom" data-testid="hero-cta-showroom" className="border border-white/70 text-white px-7 py-4 text-sm font-medium hover:bg-white/10 transition flex items-center gap-2">
              Visit the Northborough showroom
            </Link>
          </div>

          {/* Compact credibility line — visible in the first 2 seconds */}
          <div
            className="mt-10 flex flex-wrap items-center text-white/90 text-xs md:text-sm tracking-[0.15em] uppercase"
            data-testid="hero-credibility-strip"
          >
            <span>From kitchens and bathrooms to basements and attics, we cover all your home improvement needs!</span>
          </div>
        </div>
      </section>

      {/* Trust strip — credentials + facts under the hero */}
      <section className="border-y border-zinc-200 bg-white" data-testid="trust-strip">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 py-14 md:py-16">
          {/* Three fact tiles */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-10">
            {[
              { title: "Family owned & operated", body: "Servicing MetroWest Boston for over 27 years!" },
              { title: "Nearly 100 years of accumulated experience", body: "among our designer, project managers, and finish carpenters." },
              { title: "Featuring our in-house designer, PMs, craftsmen & showroom", body: "— one team creating cohesion from design to finish." },
            ].map((f, i) => (
              <div key={f.title} className="text-center border-t border-zinc-200 pt-8 md:border-t-0 md:pt-0 md:border-l md:pl-10 first:border-l-0 first:pl-0" data-testid={`trust-fact-${i}`}>
                <img src="/brand/logo.png" alt="" aria-hidden="true" className="h-7 w-auto mx-auto opacity-90" />
                <div className="font-display text-xl md:text-2xl font-light tracking-tight mt-4 leading-snug">
                  {f.title}
                </div>
                <p className="text-zinc-600 text-sm mt-2 leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>

          {/* Divider */}
          <div className="border-t border-zinc-200 mt-14 md:mt-16" />

          {/* HomeAdvisor credentials */}
          <div className="text-center mt-14 md:mt-16">
            <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">Screened · Rated · Approved</div>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-x-12 gap-y-8 md:gap-x-16">
              {[
                { src: "/photos/badges/elite_service.png",     alt: "HomeAdvisor Elite Service" },
                { src: "/photos/badges/top_rated.png",         alt: "HomeAdvisor Top Rated" },
                { src: "/photos/badges/screened_approved.png", alt: "HomeAdvisor Screened & Approved" },
              ].map((b) => (
                <img
                  key={b.src}
                  src={b.src}
                  alt={b.alt}
                  loading="lazy"
                  className="h-20 md:h-24 w-auto opacity-90 hover:opacity-100 transition"
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Marquee */}
      <section className="border-y border-zinc-200 bg-white overflow-hidden">
        <div className="flex animate-ticker whitespace-nowrap py-5 text-zinc-500 text-sm tracking-[0.25em] uppercase">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="flex gap-12 px-6">
              <span>Kitchens</span><span>·</span><span>Baths</span><span>·</span><span>Whole-Home Renovations</span>
              <span>·</span><span>Custom Cabinetry</span><span>·</span><span>Tile &amp; Stone</span>
              <span>·</span><span>Lighting</span><span>·</span><span>Licensed &amp; Insured · MA</span>
              <span>·</span>
            </div>
          ))}
        </div>
      </section>

      {/* Dual brand intro */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 py-24 md:py-32">
        <div className="grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-5">
            <div className="text-xs tracking-[0.3em] uppercase text-zinc-500">Two brands · one experience</div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-light tracking-tight mt-5 leading-[1.05]">
              Design it in the showroom.<br />Build it with our crew.
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pl-12">
            <p className="text-zinc-600 text-lg leading-relaxed">
              We are two halves of the same business. <b>Sleek Renovations</b> is our remodeling firm —
              project managers and finish carpenters who turn your house into the one you actually
              want to live in. <b>Sleek Kitchen &amp; Bath</b> is our showroom in Northborough, where
              you can touch every cabinet door and countertop before you commit a single dollar.
            </p>
            <div className="mt-10 grid sm:grid-cols-2 gap-6">
              <Link to="/services" className="group border border-zinc-200 p-8 hover:-translate-y-1 hover:shadow-lg transition" data-testid="card-remodel">
                <div className="flex items-center justify-between"><Hammer size={20} /><ArrowUpRight className="opacity-0 group-hover:opacity-100 transition" size={16} /></div>
                <div className="mt-6 font-display text-2xl">Sleek Renovations</div>
                <p className="text-zinc-600 mt-2 text-sm leading-relaxed">Full-service remodeling. Kitchens, baths, whole-home and more.</p>
              </Link>
              <Link to="/showroom" className="group border border-zinc-200 p-8 hover:-translate-y-1 hover:shadow-lg transition" data-testid="card-showroom">
                <div className="flex items-center justify-between"><Sparkles size={20} /><ArrowUpRight className="opacity-0 group-hover:opacity-100 transition" size={16} /></div>
                <div className="mt-6 font-display text-2xl">Sleek Kitchen &amp; Bath</div>
                <p className="text-zinc-600 mt-2 text-sm leading-relaxed">Showroom &amp; product catalog. Open to the public.</p>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats strip */}
      <section className="border-y border-zinc-200 bg-white">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 py-16 grid grid-cols-2 lg:grid-cols-4 gap-10">
          {STATS.map((s) => (
            <div key={s.k}>
              <div className="font-display text-4xl md:text-5xl font-light">{s.k}</div>
              <div className="text-zinc-500 text-sm mt-2 max-w-[15ch]">{s.v}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Portfolio Bento */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 py-24 md:py-32">
        <div className="flex items-end justify-between mb-12">
          <div>
            <div className="text-xs tracking-[0.3em] uppercase text-zinc-500">Selected work</div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-light tracking-tight mt-4">Recent projects.</h2>
          </div>
          <Link to="/portfolio" className="hidden md:inline-flex items-center gap-2 text-sm hover:underline" data-testid="home-portfolio-link">
            See all projects <ArrowUpRight size={14} />
          </Link>
        </div>
        <div className="grid grid-cols-6 gap-3 md:gap-4 auto-rows-[180px] md:auto-rows-[240px]">
          {portfolio.map((p, i) => {
            const layouts = [
              "col-span-6 md:col-span-4 row-span-2",
              "col-span-3 md:col-span-2 row-span-1",
              "col-span-3 md:col-span-2 row-span-1",
              "col-span-6 md:col-span-3 row-span-2",
              "col-span-6 md:col-span-3 row-span-2",
            ];
            return (
              <Link
                key={p.id}
                to={`/portfolio/${p.id}`}
                className={`relative group overflow-hidden ${layouts[i] || "col-span-3 row-span-1"}`}
                data-testid={`home-project-${i}`}
              >
                <img src={p.image} alt={p.title} style={p.image_pos ? { objectPosition: p.image_pos } : undefined} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/0 to-black/0" />
                <div className="absolute bottom-4 left-4 right-4 text-white">
                  <div className="text-[10px] tracking-[0.25em] uppercase opacity-80">{p.category}</div>
                  <div className="font-display text-lg md:text-xl mt-1">{p.title}</div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Trust */}
      <section className="bg-zinc-950 text-white">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 py-24 grid md:grid-cols-3 gap-12">
          <div className="flex items-start gap-4">
            <ShieldCheck size={28} />
            <div>
              <div className="font-display text-xl">Licensed &amp; insured</div>
              <p className="text-zinc-400 text-sm mt-2 leading-relaxed">MA HIC &amp; CSL · full liability and workers' comp on every job. Zero subcontractor surprises.</p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <PencilRuler size={24} className="mt-0.5" />
            <div>
              <div className="font-display text-xl">Customized designs</div>
              <p className="text-zinc-400 text-sm mt-2 leading-relaxed">In-house design professional with over 30 years experience.</p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <Sparkles size={28} />
            <div>
              <div className="font-display text-xl">Beautiful kitchen & bath showroom</div>
              <p className="text-zinc-400 text-sm mt-2 leading-relaxed">Touch every finish before you pick it. Cabinetry, countertops, hardware — under one roof in Northborough.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 py-24 md:py-32">
        <div className="text-xs tracking-[0.3em] uppercase text-zinc-500">Owner reviews</div>
        <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-light tracking-tight mt-4 max-w-4xl">
          Words from clients who've lived in the result.
        </h2>
        <div className="grid md:grid-cols-3 gap-6 md:gap-8 mt-14">
          {TESTIMONIALS.map((t, i) => (
            <div key={t.name} className="border border-zinc-200 p-8 md:p-10 bg-white" data-testid={`testimonial-${i}`}>
              <div className="flex gap-1 mb-6">
                {Array.from({ length: 5 }).map((_, k) => <Star key={k} size={14} fill="#09090b" stroke="#09090b" />)}
              </div>
              <p className="text-zinc-800 leading-relaxed">"{t.text}"</p>
              <div className="mt-6 text-sm">
                <div className="font-medium">{t.name}</div>
                <div className="text-zinc-500">{t.town}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA band */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 pb-24 md:pb-32">
        <div className="border border-zinc-200 bg-white p-10 md:p-16 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="text-xs tracking-[0.3em] uppercase text-zinc-500">Free, no obligation</div>
            <h3 className="font-display text-3xl md:text-4xl lg:text-5xl font-light mt-4">Let's sketch what's possible.</h3>
            <p className="text-zinc-600 mt-4 max-w-xl">Bring a Pinterest board, a rough idea, or just a pain point. We'll spend an hour with you — in your home or our showroom — and follow up with a clear scope and budget range.</p>
          </div>
          <div className="flex flex-col gap-3 md:items-end">
            <Link to="/booking" className="bg-zinc-950 text-white px-7 py-4 text-sm font-medium hover:bg-zinc-800 transition w-fit" data-testid="home-cta-bottom-booking">Book a consultation</Link>
            <a href={BUSINESS.phoneHref} className="text-sm hover:underline" data-testid="home-cta-phone">or call {BUSINESS.phone}</a>
          </div>
        </div>
      </section>
    </div>
  );
}
